import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import styles from './NotasPage.module.css';

export default function NotasPage() {
  const { id: asigId } = useParams();
  const { user }       = useAuth();
  const navigate       = useNavigate();

  const [datos, setDatos]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (user?.rol !== 'profesor') { navigate(-1); return; }
    api.get(`/asignaturas/${asigId}/resumen-notas`)
      .then(r => setDatos(r.data.data))
      .finally(() => setLoading(false));
  }, [asigId]);

  const toggle = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const notaColor = n => {
    if (n === null || n === undefined) return 'var(--text-light)';
    if (n >= 9) return '#2d7a4f';
    if (n >= 7) return '#1a3a6b';
    if (n >= 5) return '#c07a1a';
    return '#c0392b';
  };
  const notaLabel = n => {
    if (n === null || n === undefined) return '—';
    if (n >= 9) return 'Sobresaliente';
    if (n >= 7) return 'Notable';
    if (n >= 6) return 'Bien';
    if (n >= 5) return 'Aprobado';
    return 'Suspenso';
  };

  if (loading) return <div className={styles.loading}>Cargando resumen...</div>;
  if (!datos)  return <div className={styles.loading}>Sin datos.</div>;

  const { asignatura, categorias, alumnos } = datos;

  return (
    <div className={styles.page}>
      <button className={styles.backBtn} onClick={() => navigate(-1)}>← Volver</button>

      <div className={styles.header}>
        <div>
          <h2 className={styles.titulo}>📊 Resumen de notas</h2>
          <p className={styles.subtitulo}>{asignatura?.nombre} — {alumnos?.length} alumno{alumnos?.length !== 1 ? 's' : ''}</p>
        </div>
        {categorias?.length > 0 && (
          <div className={styles.categoriasResumen}>
            {categorias.map(c => (
              <span key={c.id} className={styles.categoriaChip}>
                {c.nombre} <strong>{parseFloat(c.peso).toFixed(0)}%</strong>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className={styles.alumnosList}>
        {alumnos?.length === 0 && (
          <div className={styles.empty}>No hay alumnos matriculados.</div>
        )}

        {alumnos?.map(alumno => {
          const abierto = !!expanded[alumno.id];
          const np = alumno.nota_ponderada;

          return (
            <div key={alumno.id} className={styles.alumnoCard}>
              {/* Cabecera del alumno */}
              <div className={styles.alumnoHeader} onClick={() => toggle(alumno.id)}>
                <div className={styles.alumnoAvatarWrap}>
                  {alumno.avatar_url ? (
                    <img src={alumno.avatar_url} alt="" className={styles.alumnoAvatar} />
                  ) : (
                    <div className={styles.alumnoIniciales}>
                      {alumno.nombre?.[0]}{alumno.apellidos?.[0]}
                    </div>
                  )}
                </div>
                <div className={styles.alumnoInfo}>
                  <p className={styles.alumnoNombre}>{alumno.apellidos}, {alumno.nombre}</p>
                  <p className={styles.alumnoEmail}>{alumno.email}</p>
                </div>
                <div className={styles.alumnoBadges}>
                  <span className={styles.calificadasBadge}>
                    ✅ {alumno.total_calificadas}/{alumno.total_trabajos} calificados
                  </span>
                </div>
                <div className={styles.notaFinalWrap}>
                  <div className={styles.notaCirculo} style={{ '--nc': notaColor(np) }}>
                    {np !== null ? (
                      <>
                        <span className={styles.notaNum}>{parseFloat(np).toFixed(1)}</span>
                        <span className={styles.notaLabel}>{notaLabel(np)}</span>
                      </>
                    ) : (
                      <span className={styles.notaNum} style={{ fontSize:'1rem', color:'var(--text-light)' }}>—</span>
                    )}
                  </div>
                </div>
                <span className={styles.chevron}>{abierto ? '▴' : '▾'}</span>
              </div>

              {/* Detalle expandible */}
              {abierto && (
                <div className={styles.alumnoDetalle}>
                  {alumno.categorias?.map((cat, ci) => (
                    <div key={ci} className={styles.catSection}>
                      <div className={styles.catHeader}>
                        <span className={styles.catNombre}>{cat.nombre}</span>
                        <span className={styles.catPeso}>{parseFloat(cat.peso).toFixed(0)}% de la nota final</span>
                      </div>
                      <div className={styles.trabajosList}>
                        {cat.trabajos?.map((t, ti) => (
                          <div key={ti} className={styles.trabajoRow}>
                            <span className={styles.trabajoNombre}>
                              {t.titulo}
                              {t.tipo === 'no_entregable' && <span className={styles.tipoBadge}>📋</span>}
                            </span>
                            <span className={styles.trabajoPeso}>⚖️ {parseFloat(t.peso_individual || 0).toFixed(2)}%</span>
                            <div className={styles.trabajoNota}
                              style={{ color: notaColor(t.nota !== null ? parseFloat(t.nota) : null) }}>
                              {t.nota !== null && t.nota !== undefined
                                ? <strong>{parseFloat(t.nota).toFixed(1)}</strong>
                                : <span style={{ color:'var(--text-light)' }}>—</span>
                              }
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Trabajos sin categoría */}
                  {alumno.sin_categoria?.length > 0 && (
                    <div className={styles.catSection}>
                      <div className={styles.catHeader}>
                        <span className={styles.catNombre}>Sin categoría</span>
                      </div>
                      <div className={styles.trabajosList}>
                        {alumno.sin_categoria.map((t, ti) => (
                          <div key={ti} className={styles.trabajoRow}>
                            <span className={styles.trabajoNombre}>{t.titulo}</span>
                            <span className={styles.trabajoPeso}></span>
                            <div className={styles.trabajoNota}
                              style={{ color: notaColor(t.nota !== null ? parseFloat(t.nota) : null) }}>
                              {t.nota !== null && t.nota !== undefined
                                ? <strong>{parseFloat(t.nota).toFixed(1)}</strong>
                                : <span style={{ color:'var(--text-light)' }}>—</span>
                              }
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Nota ponderada final */}
                  {np !== null && (
                    <div className={styles.notaPonderadaRow}>
                      <span>Nota final ponderada</span>
                      <strong style={{ color: notaColor(parseFloat(np)) }}>
                        {parseFloat(np).toFixed(2)} — {notaLabel(parseFloat(np))}
                      </strong>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
